import { useEffect, useState } from 'react';
import Head from 'next/head';

import { usePdp } from 'hooks/usePdp';
import { SizeGuide } from 'ui/components/Sizes';
import { useCart } from 'hooks/useCart';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useMiniCart } from 'hooks/useMiniCart';
import { extractError } from 'utils/extractError';
import { ProductCartItem } from '__generated__/graphql';
import { getSizesFromSizeGroups } from 'utils/products';
import { parseLocaleForFluidConfigure } from 'utils/personalise';

export const ProductPersonalise = () => {
  const [initPersonalizationReady, setInitPersonalizationReady] =
    useState(false);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const { masterProduct, variation, setAddToCartError } = usePdp();
  const { addToCart } = useCart({ pause: true });
  const { openMiniCart } = useMiniCart();
  const {
    currency: { code: currencyCode },
    env: { configureIdScriptUrl, configureIdApiKey },
    language,
    countryCode,
  } = useSiteConfig();

  useEffect(() => {
    const { locale } = parseLocaleForFluidConfigure(language, countryCode);
    //Init
    window.configureParams = {
      productId: masterProduct?.configureID?.productID,
      demandwareProductID: variation?.variantId,
      demandwareConfigureAPIKey: configureIdApiKey,
      currency: currencyCode,
      locale: locale,
    };

    window.openSizeChart = function () {
      setShowSizeGuide(true);
    };

    setInitPersonalizationReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Callback when a Fluid Configure product is added to cart
    window.addToCart = async function (recipe) {
      const sizes = variation
        ? getSizesFromSizeGroups(variation.sizeGroups)
        : [];

      const selectedSize = sizes.find(
        x => x.productId === recipe.data.productItemNumber
      );

      const res = await addToCart({
        swatch: variation!.colorValue,
        size: selectedSize?.id,
        quantity: Number(recipe.product_quantities.product),
        productId: selectedSize?.productId as string,
        productPersonalization:
          {
            imageURL: `${recipe.images.view_1.split(',')[0]},0.60.png`,
            largeImageURL: recipe.images.view_1,
            nameText: recipe.data.nameText,
            numberText: recipe.data.numberText,
            price: String(recipe.prices[currencyCode]),
            recipeID: String(recipe.product.id),
            quantity: recipe.custom.quantity,
          } ?? undefined,
        masterId: variation!.masterId,
      });

      if (extractError(res)) {
        setAddToCartError(extractError(res));
        return;
      }

      const items = res.data?.addItemsToCart.products?.filter(
        product =>
          product.__typename === 'ProductCartItem' &&
          product?.product.id === selectedSize?.productId
      ) as ProductCartItem[];

      if (items) openMiniCart(items);
    };
    // we should re-initialize the method so as to have cartId in the context when it's changed
  }, [addToCart, currencyCode, openMiniCart, setAddToCartError, variation]);

  return (
    <div id="fluidConfigure">
      {initPersonalizationReady && configureIdScriptUrl && (
        <Head>
          <script async src={configureIdScriptUrl} />
        </Head>
      )}
      {showSizeGuide && (
        <SizeGuide
          categoryId={masterProduct?.primaryCategoryId}
          productMeasurements={masterProduct?.productMeasurements}
          onClickOutside={() => setShowSizeGuide(false)}
          productDivision={masterProduct?.productDivision}
        />
      )}
    </div>
  );
};
